import { useEffect, useState } from 'react';
import { Card, Toggle } from '@playerbase/ui';
import type {
  DiscordGuildChannel,
  DiscordGuildRole,
  FeedbackForm,
} from '@playerbase/types';

import {
  type BuilderMetadata,
  type UseFormBuilderState,
} from '../../../games/use-form-builder-state.js';
import { DiscordChannelMultiSelect } from '../../game-settings/discord/discord-channel-multi-select.js';
import { DiscordRoleMultiSelect } from '../../game-settings/discord/discord-role-multi-select.js';
import { RecruitAccessPanel } from './channel-access-panel.js';

interface RecruitCardProps {
  metadata: BuilderMetadata;
  builder: UseFormBuilderState;
  channels: DiscordGuildChannel[];
  channelsLoading: boolean;
  roles: DiscordGuildRole[];
  rolesLoading: boolean;
  hasConnection: boolean;
  form: FeedbackForm | null;
  guildId: string | null;
}

// ---------------------------------------------------------------------------
// "Recruit into playtest" card. Rendered alongside the form-details and
// fields cards on the form builder.
//
// Recruit is a post-submission opt-in: when the studio configures at
// least one channel or role here, the bot adds an "Add me to playtest
// channels" button to the ephemeral thank-you reply. Clicking it grants
// the player access. See .docs/feedback-forms/v2-ux-and-refactor-pr-plan.md
// (Story 10) for the design.
//
// The toggle gates the pickers' visibility. It's off by default for
// brand-new forms (empty arrays) and starts on for forms that already
// have selections. Toggling off clears the arrays so what's hidden
// can't accidentally ship on save - the studio can hit "Discard" if
// they undid a selection by mistake (matches the rest of the builder's
// edits-stay-local model).
// ---------------------------------------------------------------------------
export function RecruitCard({
  metadata,
  builder,
  channels,
  channelsLoading,
  roles,
  rolesLoading,
  hasConnection,
  form,
  guildId,
}: RecruitCardProps): JSX.Element {
  const channelCount = metadata.recruitChannelIds.length;
  const roleCount = metadata.recruitRoleIds.length;
  const hasSelections = channelCount > 0 || roleCount > 0;

  // Local toggle intent. Initial state mirrors whether the form arrived
  // with selections; further changes are user-driven. Realtime reseeds
  // (or a Discard) flip the arrays back to the server snapshot - the
  // effect below keeps the toggle in sync with that source of truth so
  // the panel doesn't get stuck open with an empty selection.
  const [enabled, setEnabled] = useState<boolean>(hasSelections);
  useEffect(() => {
    if (hasSelections && !enabled) setEnabled(true);
    if (!hasSelections && enabled) {
      // Don't auto-collapse - leaving the toggle on after a clear lets
      // the studio re-pick without re-clicking the toggle. Only an
      // explicit toggle-off collapses (handler below).
    }
  }, [hasSelections, enabled]);

  function handleToggle(next: boolean): void {
    setEnabled(next);
    if (!next && hasSelections) {
      builder.updateMetadata({
        recruitChannelIds: [],
        recruitRoleIds: [],
      });
    }
  }

  return (
    <Card>
      <Card.Header className={enabled ? undefined : 'pb-6'}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-0.5">
            <Card.Title>Recruit respondents into channels or groups</Card.Title>
            <Card.Description>
              {enabled
                ? 'After they submit, players see an extra button on the thank-you message. Tapping it adds them to the channels and roles you pick here.'
                : 'Optional. Turn on to show players an "Add me to playtest channels" prompt after they submit.'}
            </Card.Description>
          </div>
          <Toggle
            checked={enabled}
            onCheckedChange={handleToggle}
            aria-label="Enable recruit opt-in"
          />
        </div>
      </Card.Header>
      {enabled ? (
        <Card.Content className="flex flex-col gap-6">
          {!hasConnection ? (
            <p className="text-sm text-text-muted">
              Connect this game to Discord first - the picker reads the bot&apos;s
              channel and role lists.
            </p>
          ) : (
            <>
              <section className="flex flex-col gap-2">
                <header className="flex items-baseline justify-between gap-3">
                  <h3 className="text-sm font-semibold text-text">Channels</h3>
                  <p className="text-xs text-text-muted">
                    {channelCount === 0
                      ? 'None selected'
                      : `${channelCount} selected`}
                  </p>
                </header>
                <p className="text-xs text-text-muted">
                  The bot grants View Channel + Send Messages on each channel.
                </p>
                <DiscordChannelMultiSelect
                  channels={channels}
                  selectedIds={metadata.recruitChannelIds}
                  onChange={(next) =>
                    builder.updateMetadata({ recruitChannelIds: next })
                  }
                  loading={channelsLoading}
                />
              </section>

              <section className="flex flex-col gap-2">
                <header className="flex items-baseline justify-between gap-3">
                  <h3 className="text-sm font-semibold text-text">Roles</h3>
                  <p className="text-xs text-text-muted">
                    {roleCount === 0 ? 'None selected' : `${roleCount} selected`}
                  </p>
                </header>
                <p className="text-xs text-text-muted">
                  The bot assigns each role to the player. Bot-managed roles
                  and @everyone are hidden.
                </p>
                <DiscordRoleMultiSelect
                  roles={roles}
                  selectedIds={metadata.recruitRoleIds}
                  onChange={(next) =>
                    builder.updateMetadata({ recruitRoleIds: next })
                  }
                  loading={rolesLoading}
                />
              </section>

              {form !== null ? (
                <RecruitAccessPanel
                  form={form}
                  channels={channels}
                  roles={roles}
                  guildId={guildId}
                />
              ) : null}
            </>
          )}
        </Card.Content>
      ) : null}
    </Card>
  );
}
